"use client";

import React, { useState, useEffect, useCallback } from "react";
import Link from "next/link";
import AppLogo from "@/components/ui/AppLogo";
import Icon from "@/components/ui/AppIcon";

interface NavItem {
  label: string;
  href: string;
  isExternal?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { label: "Home", href: "#home" },
  { label: "About", href: "#about" },
  { label: "Academics", href: "#academics" },
  { label: "Gallery", href: "#gallery" },
  { label: "Videos", href: "#videos" },
  { label: "Contact", href: "#contact" },
];

const Header: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("home");

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 60);
    const sections = ["home", "about", "academics", "gallery", "videos", "contact"];
    let current = "home";
    for (const id of sections) {
      const el = document.getElementById(id);
      if (el) {
        const rect = el.getBoundingClientRect();
        if (rect.top <= 100) current = id;
      }
    }
    setActiveSection(current);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const handleNavClick = (href: string) => {
    setMobileOpen(false);
    if (href.startsWith("#")) {
      const id = href.slice(1);
      const el = document.getElementById(id);
      if (el) {
        const offset = 80;
        const top = el.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top, behavior: "smooth" });
      }
    }
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
          scrolled
            ? "bg-[#060e1e]/97 backdrop-blur-xl shadow-lg py-2 border-b border-[#e53e3e]/20"
            : "bg-[#060e1e]/85 backdrop-blur-sm py-3"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          {/* Logo */}
          <Link href="/homepage" className="flex items-center gap-3 group" aria-label="Royal Schools Home">
            <AppLogo size={48} className="transition-transform duration-300 group-hover:scale-105" />
            <div className="hidden sm:block">
              <div className="font-heading font-800 text-[#e53e3e] text-base leading-tight tracking-wide">
                Royal Schools
              </div>
              <div className="font-heading font-500 text-white/70 text-xs leading-tight tracking-wider uppercase">
                Excellence in Education
              </div>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-6" aria-label="Main navigation">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.label}
                onClick={() => handleNavClick(item.href)}
                className={`nav-link ${activeSection === item.href.slice(1) ? "active text-primary" : ""}`}
                aria-label={`Navigate to ${item.label}`}
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* CTA + Mobile Toggle */}
          <div className="flex items-center gap-3">
            <Link
              href="/management"
              className="hidden sm:inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#e53e3e] to-[#c53030] text-white rounded-full font-heading font-700 text-sm hover:from-[#fc8181] hover:to-[#e53e3e] transition-all duration-300 shadow-primary-sm hover:shadow-primary-md hover:-translate-y-0.5"
            >
              <Icon name="LockClosedIcon" size={15} variant="solid" />
              Login
            </Link>

            {/* Mobile Hamburger */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden w-10 h-10 flex items-center justify-center rounded-xl border border-[#e53e3e]/30 bg-[#0d1a30] text-[#e53e3e] hover:bg-[#e53e3e] hover:text-white transition-all duration-300"
              aria-label="Toggle mobile menu"
              aria-expanded={mobileOpen}
            >
              <Icon name={mobileOpen ? "XMarkIcon" : "Bars3Icon"} size={20} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Mobile Menu Panel */}
      <div
        className={`mobile-menu fixed top-0 right-0 h-full w-72 z-50 bg-[#0a1525] border-l border-[#e53e3e]/20 shadow-2xl lg:hidden ${
          mobileOpen ? "open" : ""
        }`}
        role="dialog"
        aria-label="Mobile navigation menu"
      >
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <AppLogo size={40} />
              <span className="font-heading font-700 text-[#e53e3e] text-sm tracking-wide">Royal Schools</span>
            </div>
            <button
              onClick={() => setMobileOpen(false)}
              className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#0d1a30] text-[#e53e3e] border border-[#e53e3e]/20"
              aria-label="Close menu"
            >
              <Icon name="XMarkIcon" size={18} />
            </button>
          </div>

          {/* Nav Links */}
          <nav className="flex flex-col gap-1" aria-label="Mobile navigation">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.label}
                onClick={() => handleNavClick(item.href)}
                className={`text-left px-4 py-3 rounded-xl font-heading font-600 text-sm transition-all duration-200 ${
                  activeSection === item.href.slice(1)
                    ? "bg-gradient-to-r from-[#e53e3e] to-[#c53030] text-white"
                    : "text-white/80 hover:bg-[#0d1a30] hover:text-[#e53e3e]"
                }`}
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* Login CTA */}
          <div className="mt-6 pt-6 border-t border-[#e53e3e]/20">
            <Link
              href="/management"
              onClick={() => setMobileOpen(false)}
              className="flex items-center justify-center gap-2 w-full px-5 py-3 bg-gradient-to-r from-[#e53e3e] to-[#c53030] text-white rounded-xl font-heading font-700 text-sm hover:from-[#fc8181] hover:to-[#e53e3e] transition-all duration-300"
            >
              <Icon name="LockClosedIcon" size={15} variant="solid" />
              Admin Login
            </Link>
          </div>

          {/* School Info */}
          <div className="mt-6 p-4 bg-[#060e1e] rounded-xl border border-[#e53e3e]/15">
            <p className="text-xs text-[#8A9BB8] font-body leading-relaxed">
              Estd. 2003 · State Board · English Medium<br />
              KG to XII · Chennai – 600095
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;